import { TableLoader } from '@/components/shared/table/TableLoader';
import { FC, Suspense, useEffect, useState } from 'react';
import { FiAlertOctagon, FiBarChart, FiCalendar, FiClipboard, FiStar, FiUser } from 'react-icons/fi';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { NavPage } from '../../../components/page/Breadcrumbs';
import { PageContainer } from '../../../components/page/PageContainer';
import { PageSizer, SizerMode } from '../../../components/page/PageSizer';
import { SideMenuOption, VerticalMenu } from '../../../components/shared/menu/VerticalMenu';
import {
  isAdmin,
  isManagement,
  useAuthenticationService
} from '../../../services/authentication/authentication-service';
import { useFlag } from '../../../services/backstage/BackstageProvider';
import { ReadEmployee } from '../../../services/gql/graphql.generated';
import { strings as timeShiftStrings } from '../../../services/translation/strings';
import strings from '../../team/team.translations';
import { OnboardingModal } from '../onboarding/OnboardingModal';
import { calendarSectionId, TeamMemberCalendar } from './calendar/TeamMemberCalendar';
import { DangerCardSection, dangerCardSectionId } from './danger/DangerCardSection';
import { OverviewSection, overviewSectionId } from './overview/OverviewSection';
import TeamMemberSkills, { skillsSectionId } from './skills/TeamMemberSkills';
import { statsSectionId, TeamMemberStats } from './stats/TeamMemberStats';
import { TeamMemberHeader } from './TeamMemberHeader';
import { TeamMemberUpdateType, useTeamMemberService } from './TeamMemberService';
import { TeamMemberWorkRules, workRulesSectionId } from './workRules/TeamMemberWorkRules';

interface TeamMemberDisplayProps {
  userData?: ReadEmployee;
  breadcrumbs?: NavPage[];
  sendInfoCallback?: () => void;
  editMemberCard?: boolean;
  editMemberPayCard?: boolean;
  showShiftTable?: boolean;
  isMe?: boolean;
  refetchUserData?: () => void;
}

export const TeamMemberDisplay: FC<TeamMemberDisplayProps> = ({
  userData,
  breadcrumbs,
  editMemberCard,
  editMemberPayCard,
  showShiftTable,
  isMe,
  refetchUserData
}) => {
  const { userId, tabName } = useParams();
  const [searchParams] = useSearchParams();

  const updateComplete = useTeamMemberService(state => state.updateComplete);
  const updateAllNeeded = useTeamMemberService(state => state.updateNeeded === TeamMemberUpdateType.ALL);

  useEffect(() => {
    if (updateAllNeeded && refetchUserData) {
      refetchUserData();
      updateComplete(TeamMemberUpdateType.ALL);
    }
  }, [updateAllNeeded, updateComplete, refetchUserData]);

  const role = useAuthenticationService(state => state.role);

  const isAdminRole = isAdmin(role);
  const isManagerRole = isManagement(role);
  const rosterFlag = useFlag('roster');

  const [sendInfoOpen, setSendInfoOpen] = useState<boolean>(false);

  const [selectedDate, setSelectedDate] = useState<Date>(() => {
    const selectedDateParam = searchParams.get('date');
    if (selectedDateParam) {
      const [year, month, day] = selectedDateParam.split('-').map(Number);
      return new Date(Date.UTC(year, month - 1, day));
    }

    const defaultDate = new Date();
    return new Date(Date.UTC(defaultDate.getUTCFullYear(), defaultDate.getUTCMonth(), 1));
  });

  const iconStyle = 'w-4 h-4 mt-0.5';

  const overviewElement = userData && (
    <OverviewSection userData={userData} editMemberCard={editMemberCard} editMemberPayCard={editMemberPayCard} />
  );

  const skillsElement = !isMe && rosterFlag && userData ? <TeamMemberSkills employee={userData} /> : <></>;
  const dangerCardElement =
    isAdminRole && !isMe && !isMe && userData ? <DangerCardSection userData={userData} /> : <></>;

  const allElements = (
    <>
      {overviewElement}
      {/* {shiftsElement} */}
      {/* ToDo Commented out so we can look at this page whilst API is getting sorted out */}
      <TeamMemberStats selectedDate={selectedDate} setSelectedDate={setSelectedDate} />
      {userData ? <TeamMemberWorkRules employee={userData} editSchedule={editMemberCard} /> : <></>}
      {skillsElement}
      {dangerCardElement}
    </>
  );

  let tabOptions: SideMenuOption[] = [
    {
      anchorId: overviewSectionId,
      icon: <FiUser className={iconStyle} />,
      label: strings.verticalTabMenu.overviewLabel,
      element: overviewElement
    },
    // {
    //   anchorId: shiftsSectionId,
    //   icon: <FiClock className={iconStyle} />,
    //   label: strings.verticalTabMenu.shiftsLabel,
    //   element: shiftsElement
    // },
    {
      anchorId: statsSectionId,
      icon: <FiBarChart className={iconStyle} />,
      label: timeShiftStrings.timeshift.teamMember.menu.stats,
      element: <TeamMemberStats selectedDate={selectedDate} setSelectedDate={setSelectedDate} />
    },
    {
      anchorId: calendarSectionId,
      icon: <FiCalendar className={iconStyle} />,
      label: timeShiftStrings.timeshift.teamMember.menu.calendar,
      element: userData ? <TeamMemberCalendar selectedDate={selectedDate} setSelectedDate={setSelectedDate} /> : <></>
    },
    {
      anchorId: skillsSectionId,
      icon: <FiStar className={iconStyle} />,
      label: timeShiftStrings.timeshift.teamMember.menu.skills,
      element: skillsElement
    },
    {
      anchorId: workRulesSectionId,
      icon: <FiClipboard className={iconStyle} />,
      label: timeShiftStrings.timeshift.teamMember.menu.workRules,
      element: userData ? <TeamMemberWorkRules employee={userData} editSchedule={editMemberCard} /> : <></>
    }
  ];

  if (!isMe && isAdminRole && rosterFlag) {
    tabOptions.push({
      anchorId: skillsSectionId,
      icon: <FiStar className={iconStyle} />,
      label: strings.verticalTabMenu.skillsLabel,
      element: skillsElement
    });
  }

  if (isAdminRole && !isMe) {
    tabOptions.push({
      anchorId: dangerCardSectionId,
      icon: <FiAlertOctagon className={iconStyle} />,
      label: strings.verticalTabMenu.manageAccountLabel,
      element: dangerCardElement
    });
  }

  const selectedTab = tabName ?? tabOptions[0].anchorId;

  const navigate = useNavigate();

  const handleTabSelect = (anchorId: string) => {
    navigate(`/team-member/${userId}/${anchorId}`);
  };

  return (
    <PageContainer
      customHeader={
        <TeamMemberHeader
          employee={userData}
          breadcrumbs={breadcrumbs}
          sendInfoCallback={isManagerRole ? () => setSendInfoOpen(true) : undefined}
          isMe={isMe}
          selectedTab={selectedTab}
        />
      }
    >
      <PageSizer className="flex gap-5 flex-col lg:flex-row" mode={SizerMode.PAGE}>
        {userData?.isDeleted ? (
          <></>
        ) : (
          <Suspense fallback={<TableLoader />}>
            <div className="hidden lg:flex gap-5 w-full">
              <VerticalMenu
                onTabSelect={handleTabSelect}
                sideMenuOptions={tabOptions}
                isScroll={false}
                selectedTab={selectedTab}
              />
            </div>
            <div className="lg:hidden ">
              <VerticalMenu
                onTabSelect={handleTabSelect}
                sideMenuOptions={tabOptions}
                allElements={allElements}
                isScroll={true}
                selectedTab={selectedTab}
              />
            </div>
          </Suspense>
        )}
      </PageSizer>
      {userData && (
        <OnboardingModal
          user={{
            firstName: userData.firstName,
            identityId: userData.identityId!
          }}
          open={Boolean(sendInfoOpen)}
          onClose={() => {
            setSendInfoOpen && setSendInfoOpen(false);
          }}
        />
      )}
    </PageContainer>
  );
};
